/**
 * useS3Operations Hook
 * Hook for S3-specific operations (download, preview, etc.)
 */

import { useCallback } from 'react';
import { useFileService } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';

export interface UseS3OperationsReturn {
  getDownloadUrl: (fileId: string) => Promise<string | null>;
  downloadFile: (fileId: string, fileName: string) => Promise<void>;
  previewFile: (fileId: string) => Promise<string | null>;
  bulkDownload: (fileIds: string[]) => Promise<void>;
}

export const useS3Operations = (): UseS3OperationsReturn => {
  const fileService = useFileService();

  // Get download URL
  const getDownloadUrl = useCallback(
    async (fileId: string): Promise<string | null> => {
      try {
        const response = await fileService.getDownloadUrl(fileId);
        return response.downloadUrl;
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to generate download URL');
        return null;
      }
    },
    [fileService]
  );

  // Download file
  const downloadFile = useCallback(
    async (fileId: string, fileName: string) => {
      try {
        const downloadUrl = await getDownloadUrl(fileId);
        if (!downloadUrl) return;

        // Create a temporary anchor element and trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.info('Download Started', `Downloading ${fileName}`);
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to download file');
      }
    },
    [getDownloadUrl]
  );

  // Preview file (returns URL for preview)
  const previewFile = useCallback(
    async (fileId: string): Promise<string | null> => {
      try {
        const response = await fileService.getDownloadUrl(fileId);
        return response.downloadUrl;
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to generate preview URL');
        return null;
      }
    },
    [fileService]
  );

  // Bulk download
  const bulkDownload = useCallback(
    async (fileIds: string[]) => {
      try {
        const response = await fileService.bulkDownload({ fileIds });

        if (response.files.length === 0) {
          toast.warning('No Files', 'No files available for download');
          return;
        }

        // Download each file
        response.files.forEach((file, index) => {
          setTimeout(() => {
            const link = document.createElement('a');
            link.href = file.downloadUrl;
            link.download = file.fileName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }, index * 500); // Stagger downloads to avoid browser blocking
        });

        toast.info('Downloads Started', `Downloading ${response.files.length} file(s)`);
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to download files');
      }
    },
    [fileService]
  );

  return {
    getDownloadUrl,
    downloadFile,
    previewFile,
    bulkDownload,
  };
};
